import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { MouseEvent, ReactNode } from "react";
import { forwardRef } from "react";

import { t } from "@/i18n-js/instance";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

interface GetIconTypeProps {
  showDropdown: boolean;
  useEditIcon?: boolean;
  value: string;
}

const getIconType = ({
  showDropdown,
  useEditIcon,
  value,
}: GetIconTypeProps) => {
  if (showDropdown) {
    return "close";
  }
  if (useEditIcon) {
    return value.length ? "pencil" : null;
  }
  return "cheveron-down";
};

export interface InputContainerProps {
  children: ReactNode;
  disabled?: boolean;
  hideIcon?: boolean;
  inputContainerClassName?: string;
  showDropdown: boolean;
  showSearchIcon?: boolean;
  toggleDropdown: (event: MouseEvent<HTMLButtonElement>) => void;
  useEditIcon?: boolean;
  value: string;
}

export const InputContainer = forwardRef<HTMLDivElement, InputContainerProps>(
  (
    {
      toggleDropdown,
      children,
      useEditIcon,
      showDropdown,
      value,
      disabled = false,
      hideIcon = false,
      showSearchIcon = false,
      inputContainerClassName,
    },
    ref,
  ) => {
    const iconType = getIconType({ showDropdown, useEditIcon, value });
    return (
      <div
        ref={ref}
        className={classNames(
          anchorClassNames("form-control", {
            "form-control--disabled": disabled,
          }),
          {
            "focus-within:!border-light": !disabled,
          },
          "bg-primary !border-primary",
          inputContainerClassName,
        )}
      >
        {showSearchIcon && (
          <span className="flex size-4 mr-2">
            <Icon type="16-search" size={14} className="text-default" />
          </span>
        )}
        <div className={legacyClassNames("select-inputs-container")}>
          {children}
        </div>
        <button
          type="button"
          aria-label={iconType ?? t("options_dropdown")}
          className={legacyClassNames("icon-container")}
          onClick={event => {
            event.stopPropagation();
            toggleDropdown(event);
          }}
        >
          {!hideIcon && iconType && (
            <Icon type={iconType} className="text-dark" />
          )}
        </button>
      </div>
    );
  },
);
InputContainer.displayName = "InputContainer";
